import React, { createContext, useCallback, useContext, useState } from "react";

/**
 * Ultra-minimal toast — ink on paper, one line, dismisses after 6s.
 * Zero deps, matches Nav's spec.
 */
const ToastCtx = createContext(null);

let idc = 0;

export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);

  const push = useCallback((t) => {
    const id = ++idc;
    setItems((s) => [...s, { id, ...t }]);
    setTimeout(() => setItems((s) => s.filter((x) => x.id !== id)), t.ttl || 6000);
  }, []);

  const api = {
    info:    (msg, opts = {}) => push({ level: "info", msg, ...opts }),
    success: (msg, opts = {}) => push({ level: "success", msg, ...opts }),
    error:   (msg, opts = {}) => push({ level: "error", msg, ttl: 9000, ...opts }),
    dismiss: (id) => setItems((s) => s.filter((x) => x.id !== id)),
  };

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div
        style={{
          position: "fixed",
          right: 20,
          bottom: 20,
          display: "flex",
          flexDirection: "column",
          gap: 10,
          zIndex: 100,
          maxWidth: 380,
        }}
      >
        {items.map((it) => (
          <div
            key={it.id}
            data-testid={`toast-${it.level}`}
            style={{
              background: it.level === "error" ? "var(--paper-raised)" : "var(--ink)",
              color: it.level === "error" ? "var(--down)" : "var(--paper)",
              border: it.level === "error" ? "1px solid var(--down)" : "1px solid var(--ink)",
              padding: "12px 16px",
              fontSize: 13,
              letterSpacing: "0.01em",
              display: "flex",
              alignItems: "center",
              gap: 12,
              animation: "toastIn 160ms ease-out both",
            }}
          >
            <span style={{ flex: 1 }}>{it.msg}</span>
            {it.link && (
              <a
                href={it.link}
                target="_blank"
                rel="noreferrer"
                style={{
                  color: it.level === "error" ? "var(--down)" : "var(--paper)",
                  textDecoration: "underline",
                  fontSize: 11,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                View
              </a>
            )}
          </div>
        ))}
      </div>
      <style>{`
        @keyframes toastIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
      `}</style>
    </ToastCtx.Provider>
  );
}

export const useToast = () => useContext(ToastCtx);
